import * as React from 'react';
import { useTheme } from '@mui/material';
import MuiTabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Stack from '@mui/material/Stack';
import { useSearchParams, Outlet } from 'react-router-dom';
import * as Device from 'react-device-detect';

import { SearchEventForm, SearchSpotForm, SearchMemberForm } from '@components';
import { SearchResults } from './SearchResults';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  style?: React.CSSProperties;
}

function samePageLinkNavigation(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
  if (
    event.defaultPrevented ||
    event.button !== 0 || // ignore everything but left-click
    event.metaKey ||
    event.ctrlKey ||
    event.altKey ||
    event.shiftKey
  ) {
    return false;
  }
  return true;
}

const SearchTabPanel = React.memo((props: TabPanelProps) => {
  const { children, value, index, style, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`search-tabpanel-${index}`}
      aria-labelledby={`search-tab-${index}`}
      style={{ ...style }}
      {...other}
    >
      {value === index && children}
    </div>
  );
});

interface LinkTabProps {
  label?: string;
  href: string;
  selected?: boolean;
}

function LinkTab(props: Readonly<LinkTabProps>) {
  return (
    <Tab
      component="a"
      onClick={(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        event.preventDefault();
      }}
      aria-current={props.selected && 'page'}
      {...props}
    />
  );
}

interface SearchCardProps {
  children: React.ReactNode;
}

export const SearchCard = (props: Readonly<SearchCardProps>) => {
  const { children } = props;
  const theme = useTheme();

  return <Stack sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 1 }}>{children}</Stack>;
};

export const SearchPage = React.memo(() => {
  const [tab, setTab] = React.useState(0);
  const theme = useTheme();
  let [searchParams, setSearchParams] = useSearchParams();

  const validTabs: { [key: number]: string } = {
    0: 'spots',
    1: 'events',
    2: 'members',
  };

  const handleChangeTab = (event: React.SyntheticEvent, newTab: number) => {
    const currentPath = location.pathname + (location.search ?? '');
    // event.type can be equal to focus with selectionFollowsFocus.
    if (
      event.type !== 'click' ||
      (event.type === 'click' &&
        samePageLinkNavigation(event as React.MouseEvent<HTMLAnchorElement, MouseEvent>))
    ) {
      if (Object.keys(validTabs).includes(newTab.toString())) {
        const location = searchParams.get('location');

        setSearchParams(
          { search: validTabs[newTab], page: '1', ...(location && { location }) },
          {
            state: { from: currentPath },
            replace: true,
            flushSync: true,
            viewTransition: true,
          }
        );
      }
    }
  };

  // set tab based on search param
  React.useEffect(() => {
    const currentPath = location.pathname + (location.search ?? '');
    if (!Device.isDesktop) return;

    const search = searchParams.get('search');
    const idx = search ? Object.values(validTabs).indexOf(search) : -1;

    if (idx !== -1) {
      setTab(idx);
    } else if (!['e', 's', 'c'].some((key) => searchParams.has(key))) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('search', 'spots');
      nextParams.set('page', '1');

      setSearchParams(nextParams, {
        state: { from: currentPath },
        replace: true,
        viewTransition: true,
      });
    }
  }, [searchParams]);

  if (Device.isDesktop) {
    return (
      <Stack
        direction="row"
        width="100%"
        height="100%"
        p={0}
        m={0}
        sx={{
          overflowY: 'hidden',
        }}
      >
        <Stack
          width="100%"
          height="100%"
          sx={{
            overflowY: 'auto',
          }}
        >
          <SearchResults search={searchParams.get('search')} />
        </Stack>
        <Stack
          width={500}
          height="100%"
          sx={{
            backgroundColor: theme.palette.background.default,
            boxShadow: '0 10px 20px rgba(0, 0, 0, .5)',
            overflowY: 'auto',
            '& > *': {
              scrollbarWidth: 'none', // hide scrollbar Firefox
              msOverflowStyle: 'none', // hide scrollbar IE and Edge
            },
            '&::-webkit-scrollbar': {
              display: 'none', // hide scrollbar Chrome, Safari
            },
          }}
        >
          <MuiTabs
            value={tab}
            onChange={handleChangeTab}
            aria-label="search tabs"
            centered
            indicatorColor="primary"
            textColor="inherit"
            variant="fullWidth"
          >
            <LinkTab label="Spots" href="/search?search=spots" selected={tab === 0} />
            <LinkTab label="Events" href="/search?search=events" selected={tab === 1} />
            <LinkTab label="Members" href="/search?search=members" selected={tab === 2} />
          </MuiTabs>
          <SearchTabPanel value={tab} index={0} style={{ padding: 10, height: '100vh' }}>
            <SearchSpotForm expanded={false} setExpanded={() => {}} />
          </SearchTabPanel>
          <SearchTabPanel value={tab} index={1} style={{ padding: 10, height: '100vh' }}>
            <SearchEventForm expanded={false} setExpanded={() => {}} />
          </SearchTabPanel>
          <SearchTabPanel value={tab} index={2} style={{ padding: 10, height: '100vh' }}>
            <SearchMemberForm expanded={false} setExpanded={() => {}} />
          </SearchTabPanel>
        </Stack>
        <Outlet />
      </Stack>
    );
  }

  return (
    <Stack id="search-page" spacing={2} p={2}>
      <SearchResults search={searchParams.get('search')} />
      <Outlet />
    </Stack>
  );
});
